import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Pagination } from "@mui/material";
import { Edit2 } from "lucide-react";

import BaseTable from "@reusables/BaseTable";
import BaseChip from "@reusables/BaseChip";
import BaseMaterialIconButton from "@reusables/BaseMaterialIconButton";
import NoPermissionBanner from "@components/ErrorPages/NoPermissionBanner";
import ReservationModal from "./modals/ReservationModal";

import { jsxSwitch, useOrdering, usePagination } from "@helpers/utils";
import { flattenLines } from "@components/Dashboard/pages/Sales/Orders/utils";
import { useGetModuleSaleOrdersFullQuery } from "@redux/features/sales/orders/salesOrdersApi";
import { useGetProductReservationsQuery } from "@redux/features/products/productsApi";
import { Product, SaleOrder } from "@/types/general";
import { Can } from "@/casl.config";
import { useIsReservationsEnabled } from "@redux/features/companies/useIsReservationsEnabled";

interface SaleOrdersTabProperties {
    product: Product.Extended;
}

interface ReservationModalState {
    order: SaleOrder.Root;
    reservation?: Product.Augmentations.Reservation.Root;
}

export default function SaleOrdersTab(props: SaleOrdersTabProperties) {
    const { t } = useTranslation("", { keyPrefix: "products.viewEditProduct.tabs.saleOrder" });

    // Check if reservations feature is enabled in company settings
    const isReservationsEnabled = useIsReservationsEnabled();

    const pagination = usePagination({ page: 1, limit: 8 });

    const { orderBy, setOrderBy } = useOrdering<SaleOrder.DTO.OrderBy>({ name: "id", type: "desc" });

    const { data: salesResponse, isLoading: isSalesResponseLoading } = useGetModuleSaleOrdersFullQuery({
        filters: {
            products: [props.product.id]
        },
        orderBy,
        pagination,
        module: "product"
    });

    // Fetch product reservations separately for accurate data
    // Skip this query if the reservations feature is disabled to avoid unnecessary API calls
    const { data: reservationsData } = useGetProductReservationsQuery({
        product_id: props.product.id
    }, {
        skip: !isReservationsEnabled
    });

    // Create a lookup map for O(1) access: orderId -> reservation
    const reservationsMap = useMemo(() => {
        const map = new Map<number, Product.Augmentations.Reservation.Root>();
        reservationsData?.forEach(reservation => {
            map.set(reservation.sale_order.id, reservation);
        });
        return map;
    }, [reservationsData]);

    const [isReservationModalOpen, setIsReservationModalOpen] = React.useState(false);
    const [reservationModalState, setReservationModalState] = React.useState<ReservationModalState | null>(null);

    const handleOpenReservationModal = (row: SaleOrder.Root) => {
        const reservation = reservationsMap.get(row.id);
        setReservationModalState({
            order: row,
            reservation
        });

        setIsReservationModalOpen(true);
    };

    return (
        <>
            <Can not I="sale_orders.view" a="product">
                <NoPermissionBanner />
            </Can>

            <Can I="sale_orders.view" a="product">
                <BaseTable
                    isDataLoading={isSalesResponseLoading}
                    data={salesResponse?.payload ?? []}
                    columns={[
                        {
                            header: t("table.columns.0"),
                            getter: (row) => <Link to={`/dashboard/sales/orders/${row.id}/details`}><u
                                className={"font-semibold"}>{row.code ?? row.id}</u></Link>
                        },
                        {
                            header: t("table.columns.1"),
                            getter: (row) => <Link
                                to={`/dashboard/customers/${row.customer.id}/details`}><u>{row.customer.name}</u></Link>,
                            comparator: () => 0
                        },
                        {
                            header: t("table.columns.2"),
                            getter: (row) => <>{flattenLines(row.lines).filter(o => o.data.product.id === props.product.id).reduce((cum, curVal) => cum + curVal.data.quantity, 0)}</>
                        },
                        {
                            header: t("table.columns.3"),
                            getter: (row) => jsxSwitch(
                                {
                                    0: <BaseChip
                                        className={"bg-[#F3F2EF]"}>{t(`table.invoicingStatuses.notInvoiced`)}</BaseChip>,
                                    1: <BaseChip fill={"green"}>{t(`table.invoicingStatuses.invoiced`)}</BaseChip>
                                },
                                row.is_invoiced ? 1 : 0
                            )
                        },
                        {
                            header: t("table.columns.4"),
                            getter: (row) => <>{row.order_date.format("DD.MM.YYYY")}</>,
                            comparator: () => 0
                        },
                        // Reservations column - only shown when feature is enabled
                        ...(isReservationsEnabled ? [{
                            header: t("table.columns.5"),
                            getter: (row: SaleOrder.Root) => {
                                const reservation = reservationsMap.get(row.id);
                                return (
                                    <div className="flex items-center space-x-2">
                                        {/* TODO: this is ?perhaps? a temporary fix since we now receive an array of reservations even though it is not possible to have multiple reservations per product */}
                                        <span>{reservation ? reservation.reservations.reduce((cum, curVal) => cum + curVal.quantity, 0) : "-"}</span>
                                        <BaseMaterialIconButton
                                            icon={<Edit2 size={16} />}
                                            onClick={() => handleOpenReservationModal(row)}
                                        />
                                    </div>
                                );
                            }
                        }] : [])
                    ]}
                    manualControls={{
                        ordering: (newOrdering) => {
                            if (newOrdering) {
                                let name: SaleOrder.DTO.OrderBy | undefined;

                                switch (newOrdering?.index) {
                                    case 1:
                                        name = "customer";
                                        break;
                                    case 4:
                                        name = "date";
                                        break;
                                    default:
                                        name = undefined;
                                }

                                if (name)
                                    setOrderBy({
                                        name,
                                        type: newOrdering.order
                                    });
                            } else {
                                setOrderBy(undefined);
                            }
                        }
                    }}
                    alternate
                />
                <Pagination className="mt-[32px]" {...pagination.adapt(salesResponse)} />
            </Can>

            {/* Reservation modal - only rendered when feature is enabled */}
            {isReservationsEnabled && (
                <ReservationModal
                    isOpen={isReservationModalOpen}
                    onClose={() => setIsReservationModalOpen(false)}
                    productId={props.product.id}
                    productName={props.product.name}
                    orderId={reservationModalState?.order?.id}
                    orderCode={reservationModalState?.order?.code}
                    reservation={reservationModalState?.reservation}
                />
            )}
        </>
    );
}
